import React from "react";
import { GEO_STYLES } from "@/lib/geo";
import type { Room, Geometry } from "@/api/types";

interface Props {
  rooms: Room[];
  geoms: Record<string, Geometry | null>;
  className?: string;
}

// Lay rooms side-by-side on X (matches backend _room_offsets) into one coordinate space.
export const CombinedPlan: React.FC<Props> = ({ rooms, geoms, className = "" }) => {
  const placed: { room: Room; g: Geometry; ox: number; oy: number }[] = [];
  let cursor = 0;
  for (const r of rooms) {
    const g = geoms[r._id];
    if (!g || !g.vertices?.length) { cursor += 5; continue; }
    const xs = g.vertices.map((v) => v.x);
    const ys = g.vertices.map((v) => v.y);
    const minx = Math.min(...xs), maxx = Math.max(...xs), miny = Math.min(...ys);
    placed.push({ room: r, g, ox: cursor - minx, oy: -miny });
    cursor += (maxx - minx) + 1.0;
  }

  if (!placed.length) {
    return <div className={`flex h-48 items-center justify-center rounded-lg bg-background/40 text-sm text-muted-foreground ${className}`}>Aucune géométrie à assembler. Reconstruisez les pièces.</div>;
  }

  const allX: number[] = [], allY: number[] = [];
  placed.forEach((p) => p.g.vertices.forEach((v) => { allX.push(v.x + p.ox); allY.push(v.y + p.oy); }));
  const minX = Math.min(...allX), maxX = Math.max(...allX);
  const minY = Math.min(...allY), maxY = Math.max(...allY);
  const pad = Math.max(maxX - minX, maxY - minY) * 0.08 + 0.5;
  const vbW = maxX - minX + pad * 2;
  const vbH = maxY - minY + pad * 2;

  return (
    <svg
      data-testid="combined-plan-svg"
      viewBox={`${minX - pad} ${minY - pad} ${vbW} ${vbH}`}
      className={`w-full rounded-lg bg-[#0B111E] tech-grid ${className}`}
      style={{ aspectRatio: `${vbW} / ${vbH}` }}
    >
      {placed.map(({ room, g, ox, oy }) => {
        const style = GEO_STYLES[g.status];
        const vmap = Object.fromEntries(g.vertices.map((v) => [v.id, v]));
        const cx = g.vertices.reduce((s, v) => s + v.x, 0) / g.vertices.length + ox;
        const cy = g.vertices.reduce((s, v) => s + v.y, 0) / g.vertices.length + oy;
        return (
          <g key={room._id}>
            <polygon points={g.vertices.map((v) => `${v.x + ox},${v.y + oy}`).join(" ")} fill={style.fill} stroke="none" />
            {g.walls.map((w: any) => {
              const a = vmap[w.from], b = vmap[w.to];
              if (!a || !b) return null;
              return <line key={w.id} x1={a.x + ox} y1={a.y + oy} x2={b.x + ox} y2={b.y + oy} stroke={style.stroke} strokeWidth={3} strokeLinecap="round" vectorEffect="non-scaling-stroke" strokeDasharray={style.dash === "none" ? undefined : style.dash} />;
            })}
            <text x={cx} y={cy} textAnchor="middle" fill="#F8FAFC" fontSize={Math.max(vbW, vbH) * 0.028} fontFamily="JetBrains Mono, monospace" style={{ userSelect: "none" }}>{room.name}</text>
          </g>
        );
      })}
    </svg>
  );
};
